import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import {
    CreateChancel,
    Input,
    SelectAutocompleteMultiple,
    MaskInputCustom,
    CheckboxCustom,
    DateCalendarForm,
    SwitchCustomized,
} from 'components';
import { adminActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import {
    FindLoad,
    FindErrorItem,
    enumValues,
    states,
    FindSuccessItem,
    useModal,
    languagesById,
    RolePermission,
    PermissionList,
} from 'utils';
import { residencyEnumTypes } from './constants';
import { staffCoreCommonStyle } from './style';

export const StaffDetails = ({ staffGeneral }) => {
    const ACTION_TYPE = 'CREATE_ADMIN_DETAILS';
    const { close } = useModal();
    const classes = staffCoreCommonStyle();
    const dispatch = useDispatch();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const { handleSubmit, control, clearErrors, reset, watch, setError } = useForm({
        defaultValues: {
            license: {
                driverLicense: '',
                expireDate: '',
                state: '',
            },
            gender: '',
            birthday: '',
            residency: '',
            ssn: '',
            DOJ: {
                type: 'DOJ',
                startDate: '',
                expireDate: '',
                checked: false,
            },

            TB: {
                type: 'TB',
                startDate: '',
                expireDate: '',
                checked: false,
            },

            languages: [],
            npi: '',
            taxonomyCode: '',
            requiresSupervision: false,
            minSupervisionPercent: '',
            minContactsPerMonth: '',
        },
    });

    const checkLicense = watch('license.driverLicense') || watch('license?.state') || watch('license?.driverLicense');

    useEffect(() => {
        if (backError?.error?.length) {
            const licenseError = backError?.error[0]?.match(/license\.For/);
            const expirationDateError = backError?.error[0]?.match(/license\.Expiration/);

            if (backError?.error[0] === 'Social security number is incorrect') {
                setError('ssn', {
                    message: 'Social security number is incorrect.',
                });
                dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            }
            if (licenseError) {
                setError('license.driverLicense', {
                    message: backError?.error[0],
                });
                dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            }
            if (expirationDateError) {
                setError('license.expireDate', {
                    message: backError?.error[0],
                });
                dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            }
        }
    }, [backError]);

    useEffect(() => {
        if (staffGeneral?.id) {
            const params = {};
            const getDOJ = staffGeneral?.credentials?.find((i) => i?.type === 'DOJ');
            const getTB = staffGeneral?.credentials?.find((i) => i?.type === 'TB');
            if (staffGeneral?.license?.expireDate) {
                params.license = {
                    ...staffGeneral?.license,
                    state: states.find(({ id }) => id === staffGeneral?.license?.state),
                    expireDate: moment.utc(staffGeneral?.license?.expireDate).format().substring(0, 10),
                };
            }

            if (getDOJ?.checked) {
                params.DOJ = {
                    ...staffGeneral?.credentials?.find((i) => i?.type === 'DOJ'),
                    startDate: moment.utc(getDOJ?.startDate).format()?.substring(0, 10),
                };
            }

            if (getTB?.checked) {
                params.TB = {
                    ...staffGeneral?.credentials?.find((i) => i?.type === 'TB'),
                    startDate: moment.utc(getTB?.startDate).format()?.substring(0, 10),
                    expireDate: moment.utc(getTB?.expireDate).format()?.substring(0, 10),
                };
            }

            if (staffGeneral?.npi) params.npi = staffGeneral?.npi;
            if (staffGeneral?.taxonomyCode) params.taxonomyCode = staffGeneral?.taxonomyCode;
            if (staffGeneral?.ssn) params.ssn = staffGeneral?.ssn;
            if (staffGeneral?.residency) params.residency = staffGeneral?.residency;
            if (staffGeneral?.gender) params.gender = staffGeneral?.gender;
            if (staffGeneral?.birthday) params.birthday = moment.utc(staffGeneral?.birthday).format().substring(0, 10);
            if (staffGeneral?.languages) params.languages = staffGeneral?.languages?.map((i) => ({ id: i, label: i }));
            if (staffGeneral?.requiresSupervision) params.requiresSupervision = staffGeneral?.requiresSupervision;
            if (staffGeneral?.minSupervisionPercent != null) params.minSupervisionPercent = staffGeneral?.minSupervisionPercent;
            if (staffGeneral?.minContactsPerMonth != null) params.minContactsPerMonth = staffGeneral?.minContactsPerMonth;

            reset(params);
        }
    }, [staffGeneral]);

    useEffect(() => {
        if (success?.type === ACTION_TYPE) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        return () => {
            clearErrors();
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    function handleCreate(data) {
        const sendData = {};
        const checkLicense = data?.license?.driverLicense && data?.license?.state && data?.license?.expireDate;

        if (checkLicense) {
            sendData.license = {
                driverLicense: data?.license?.driverLicense,
                expireDate: data?.license?.expireDate && new Date(data?.license?.expireDate).toISOString(),
                state: data?.license?.state?.id ? data?.license?.state?.id : data?.license?.state,
            };
        }
        if (data?.npi) sendData.npi = data.npi;
        if (data?.taxonomyCode) sendData.taxonomyCode = data.taxonomyCode;
        if (data?.ssn) sendData.ssn = data.ssn;
        if (data?.gender) sendData.gender = data.gender;
        if (data?.birthday) sendData.birthday = data?.birthday;
        if (data?.residency) sendData.residency = data.residency;
        if (data?.languages.length) sendData.languages = data?.languages?.map((i) => i?.id);
        sendData.requiresSupervision = !!data?.requiresSupervision;
        if (data?.requiresSupervision && data?.minSupervisionPercent !== '' && data?.minSupervisionPercent != null) {
            sendData.minSupervisionPercent = Number(data.minSupervisionPercent);
        }
        if (data?.requiresSupervision && data?.minContactsPerMonth !== '' && data?.minContactsPerMonth != null) {
            sendData.minContactsPerMonth = Number(data.minContactsPerMonth);
        }

        // if (data?.DOJ?.checked || data?.TB?.checked) {
            sendData.credentials = [];
            if (data?.DOJ?.checked) {
                sendData.credentials.push({
                    type: 'DOJ',
                    checked: true,
                    startDate: data?.DOJ?.startDate && new Date(data?.DOJ?.startDate)?.toISOString(),
                });
            }else{
               sendData.credentials.push({
                   type: 'DOJ',
                   checked: false,
                   startDate: null,

               })
            }
            if (data?.TB?.checked) {
                sendData.credentials.push({
                    type: 'TB',
                    checked: true,
                    startDate: data?.TB?.startDate && new Date(data?.TB?.startDate)?.toISOString(),
                    expireDate: data?.TB?.expireDate && new Date(data?.TB?.expireDate)?.toISOString(),
                });
            }else{
                sendData.credentials.push({
                    type: 'TB',
                    checked: false,
                    startDate: null,
                    expireDate: null,
                });
            }
        // }
        dispatch(adminActions.createAdminDetails(staffGeneral?.id, sendData));
    }

    return (
        <form onSubmit={handleSubmit(handleCreate)} style={{ width: 470 }}>
            <p className="modal-header-title">{staffGeneral?.residency ? 'Edit Other Details' : 'Add Other Details'}</p>
            <div className="modal-body-wrapper">
                <p className={classes.otherDetailsTitle}>Driver’s License</p>
                <Input
                    name="license.driverLicense"
                    label={checkLicense ? 'License Number*' : 'License Number'}
                    variant="outlined"
                    control={control}
                    maxLength={20}
                    rules={{
                        maxLength: {
                            value: 20,
                            message: 'Max length is 20 digits.',
                        },
                        required: {
                            value: checkLicense,
                            message: 'Driver license is required.',
                        },
                    }}
                />
                <div className={classes.flexContainer} style={{ gap: '16px' }}>
                    <SelectAutocompleteMultiple
                        name="license.state"
                        label={checkLicense ? 'Issuing State*' : 'Issuing State'}
                        control={control}
                        options={states}
                        rules={{
                            required: {
                                value: checkLicense,
                                message: 'Issuing state is required.',
                            },
                        }}
                        renderValue={(option) => option?.name}
                    />
                    <DateCalendarForm
                        min={moment.utc().format('YYYY-MM-DD')}
                        // min={staffGeneral?.id ? null : moment.utc().format('YYYY-MM-DD')}
                        label={checkLicense ? 'Expiration Date*' : 'Expiration Date'}
                        name={'license.expireDate'}
                        control={control}
                        type={'modalInput'}
                        rules={{
                            required: {
                                value: checkLicense,
                                message: 'Expiration date is required.',
                            },
                        }}
                    />
                </div>
                <p className={`${classes.otherDetailsTitle} ${classes.titlePadding}`}>Other</p>
                <Input
                    name="npi"
                    label={'NPI Number'}
                    variant="outlined"
                    control={control}
                    maxLength={20}
                    rules={{
                        maxLength: {
                            value: 20,
                            message: 'Max length is 20 digits.',
                        },
                    }}
                />
                <Input
                    name="taxonomyCode"
                    label={'Taxonomy Code'}
                    variant="outlined"
                    control={control}
                    maxLength={20}
                    rules={{
                        maxLength: {
                            value: 20,
                            message: 'Max length is 20 characters.',
                        },
                    }}
                />
                <SelectAutocompleteMultiple
                    name="residency"
                    label={'Residency Status'}
                    options={residencyEnumTypes}
                    control={control}
                    renderValue={(option) => option?.label}
                />
                {RolePermission([PermissionList.STAFF_SSN_VIEW?.code]) && (
                    <MaskInputCustom
                        name="ssn"
                        label="SSN Number"
                        mask={'999-99-9999'}
                        variant="outlined"
                        control={control}
                        originalValue={true}
                    />
                )}
                <div className={classes.flexContainer} style={{ gap: '16px' }}>
                    <SelectAutocompleteMultiple
                        options={enumValues.GENDER_OPTIONS}
                        label={'Gender'}
                        name="gender"
                        control={control}
                        renderValue={(option) => option?.label}
                    />
                    <DateCalendarForm label={'Date of Birth'} name={'birthday'} control={control}
                                      max={moment.utc().format('YYYY-MM-DD')} />
                </div>
                <SelectAutocompleteMultiple
                    options={languagesById || []}
                    multiple={true}
                    name="languages"
                    label={'Languages Spoken'}
                    selectedType={'id'}
                    control={control}
                    renderValue={(option) => option?.label}
                />
                <p className={`${classes.otherDetailsTitle} ${classes.titlePadding}`}>Credentials</p>
                <div className={classes.flexAble} style={{ gap: '16px', alignItems: 'center' }}>
                    <CheckboxCustom
                        name="DOJ.checked"
                        label="DOJ"
                        control={control}
                        wrapperStyles={{ marginBottom: '18px', minWidth: '71px' }}
                    />
                    <DateCalendarForm
                        control={control}
                        name="DOJ.startDate"
                        label={watch('DOJ')?.checked ? 'Select the Date*' : 'Select the Date'}
                        disabled={!watch('DOJ.checked')}
                        rules={{
                            required: {
                                value: watch('DOJ.checked'),
                                message: 'DOJ date is required.',
                            },
                        }}
                    />
                </div>
                <div className={classes.flexAble} style={{ gap: '16px', alignItems: 'center' }}>
                    <CheckboxCustom
                        name="TB.checked"
                        label="TB"
                        control={control}
                        wrapperStyles={{ marginBottom: '18px', minWidth: '71px' }}
                    />
                    <DateCalendarForm
                        control={control}
                        name="TB.startDate"
                        label={watch('TB.checked') ? 'Start Date*' : 'Start Date'}
                        disabled={!watch('TB.checked')}
                        max={watch('TB.expireDate') ? watch('TB.expireDate') : ''}
                        rules={{
                            required: {
                                value: watch('TB.checked'),
                                message: 'TB start date is required.',
                            },
                        }}
                    />
                    <DateCalendarForm
                        name="TB.expireDate"
                        label={watch('TB.checked') ? 'Exp.Date*' : 'Exp.Date'}
                        min={watch('TB.startDate') ? watch('TB.startDate') : ''}
                        disabled={!watch('TB')?.checked}
                        control={control}
                        rules={{
                            required: {
                                value: watch('TB.checked'),
                                message: 'TB end date is required.',
                            },
                        }}
                    />
                </div>

                <div className="space-between-align-center" style={{ gap: 8, marginBottom: watch('requiresSupervision') ? 8 : 20 }}>
                    <div className='requires-supervision-box'>
                        <p>Requires Supervision</p>
                        <span>Staff member will be included in supervision tracking</span>
                    </div>
                    <SwitchCustomized
                        name="requiresSupervision"
                        control={control}
                    />
                </div>
                {watch('requiresSupervision') && (
                    <div style={{ marginBottom: 20 }}>
                        <div className={classes.flexContainer} style={{ gap: '16px' }}>
                            <Input
                                name="minSupervisionPercent"
                                label="Minimum Supervision %"
                                variant="outlined"
                                control={control}
                                type="number"
                                min={0}
                                max={100}
                                placeholder="Org default"
                            />
                            <Input
                                name="minContactsPerMonth"
                                label="Minimum Contacts per Month"
                                variant="outlined"
                                control={control}
                                type="number"
                                min={0}
                                placeholder="Org default"
                            />
                        </div>
                        <p className={classes.supervisionHelperText}>
                            % of monthly service hours that must be supervised (BACB standard: 5%)
                        </p>
                        <p className={classes.supervisionHelperText}>
                            Min # of supervision sessions required per month (BACB standard: 2)
                        </p>
                    </div>
                )}
            </div>
            <CreateChancel
                type="submit"
                loader={!!loader.length}
                buttonWidth="100%"
                create={'Save'}
                chancel={'Cancel'}
                onClose={close}
            />
        </form>
    );
};
